import { useCallback, useEffect, useMemo, useState } from 'react';
import '../backlog/backlog.css';
import './siteSchedFreq.css';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { triggerGridExport } from '../../components/AppToolbar/ExportMenu';
import { FormTitle } from '../../components/FormTitle';
import { CheckedCombo } from '../../components/CheckedCombo';
import { useNotify } from '../../components/notifications';
import { runLoading } from '../../components/loading';
import { fmtLocalDate } from '../../utils/datetime';
import {
  FREQ_DEFS, FREQ_LABEL,
  type SchedFreqRow, type FreqKey,
} from './schedulingFrequencyData';

// ─────────────────────────────────────────────────────────────────────────────
// Sample Scheduling → Site Scheduling Frequency  (read-only)
//
// Each non-retired COC's recurring sampling schedule. Data comes from
// GET /api/v1/site-scheduling-frequency (COCSettings + Customer/Project/
// VisualMatrix/TurnAroundTime + Priority from TaskRecurranceSetup + a best-effort
// recurrence parsed from the DevExpress Event.RecurrenceInfoXml). Retired COCs are
// excluded server-side. Rendered with the shared components (FormTitle /
// AppToolbar / AppDataGrid / CheckedCombo), matching the approved prototype.
// ─────────────────────────────────────────────────────────────────────────────

/** One row as the API serialises SiteSchedulingFrequencyRow (camelCase). */
interface SchedFreqApiRow {
  recurOid: string; cocOid: string; cocId: string | null; name: string | null; client: string | null; matrix: string | null;
  recurrence: string | null; freqKey: string | null; frequency: string | null;
  tat: string | null; daysAdv: number | null; priority: string | null;
  nextUpcoming: string | null; status: string | null;
  projectId: string | null; projectName: string | null; projectCode: string | null;
}

/** Map an API row onto the grid's SchedFreqRow. Empty frequency → the
 *  "Unscheduled" bucket so those COCs still land in a chip. */
function mapRow(a: SchedFreqApiRow, index: number): SchedFreqRow {
  const fk = (a.freqKey && a.freqKey.trim() ? a.freqKey : 'review') as FreqKey;
  return {
    // Key on the recurrence-setup Oid — the true unique row key (the SP emits one
    // row per setup, so a COC with 2 setups has 2 rows sharing cocOid). COC_ID is
    // NOT unique and must never be the row key.
    rk: a.recurOid || a.cocOid || `${a.cocId ?? ''}#${index}`,
    cocId: a.cocId ?? '',
    name: a.name ?? '',
    isLabel: false,
    client: a.client ?? '',
    matrix: a.matrix ?? '',
    recurrence: a.recurrence ?? '',
    freqKey: fk,
    frequency: (a.frequency && a.frequency.trim()) || (fk === 'review' ? 'Unscheduled' : (FREQ_LABEL[fk] ?? '')),
    tat: a.tat ?? '',
    daysAdv: a.daysAdv != null ? String(a.daysAdv) : '',
    priority: a.priority ?? '',
    nextUpcoming: a.nextUpcoming ? (fmtLocalDate(a.nextUpcoming) || '') : '',
    status: a.status ?? '',
    projectId: a.projectId ?? '',
    projectName: a.projectName ?? '',
    projectCode: a.projectCode ?? '',
  };
}

/** Muted-grey secondary text cell (prototype .grid td.muted). */
const muted = (v: string) => <span className="ssf-cell-muted">{v}</span>;

export default function SiteSchedulingFrequencyPage() {
  const notify = useNotify();

  // Multi-select filters (prototype parity) + free-text search + freq chip.
  const [clientSel, setClientSel] = useState<string[]>([]);
  const [projectSel, setProjectSel] = useState<string[]>([]);
  const [siteSel, setSiteSel] = useState<string[]>([]);
  const [freqSel, setFreqSel] = useState<FreqKey[]>([]);
  const [statusSel, setStatusSel] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<SchedFreqRow[]>([]);
  const [visibleRows, setVisibleRows] = useState<SchedFreqRow[]>([]);
  const ALL_ROWS = rows;

  // ── Load each non-retired COC's schedule from the DB ──
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/site-scheduling-frequency');
      const data = (res.data?.data ?? []) as SchedFreqApiRow[];
      setRows(data.map(mapRow));
      setLoadMs(Math.round(performance.now() - t0));
    } catch {
      setError('Could not load site scheduling frequency.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // Cascaded option lists (Excel-style): each dropdown lists only the values still
  // available given the OTHER active filters, so its trigger count ("All client (2)")
  // reflects what's actually pickable. (Free-text search is not part of the cascade.)
  const passesExceptS = useCallback((r: SchedFreqRow, except: string): boolean => {
    if (except !== 'client' && clientSel.length && !clientSel.includes(r.client)) return false;
    if (except !== 'project' && projectSel.length && !projectSel.includes(r.projectId)) return false;
    if (except !== 'site' && siteSel.length && !siteSel.includes(r.name)) return false;
    if (except !== 'freq' && freqSel.length && !freqSel.includes(r.freqKey)) return false;
    if (except !== 'status' && statusSel.length && !statusSel.includes(r.status)) return false;
    return true;
  }, [clientSel, projectSel, siteSel, freqSel, statusSel]);

  const optsExceptS = useCallback((except: string, val: (r: SchedFreqRow) => string): string[] => {
    const seen = new Set<string>();
    for (const r of ALL_ROWS) if (passesExceptS(r, except)) { const v = val(r); if (v) seen.add(v); }
    return [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [ALL_ROWS, passesExceptS]);

  const clientOpts = useMemo(() => optsExceptS('client', (r) => r.client), [optsExceptS]);
  const projectOpts = useMemo(() => optsExceptS('project', (r) => r.projectId), [optsExceptS]);
  const siteOpts = useMemo(() => optsExceptS('site', (r) => r.name), [optsExceptS]);
  const statusOpts = useMemo(() => optsExceptS('status', (r) => r.status), [optsExceptS]);
  const freqOpts = useMemo(() => {
    const keys = new Set(ALL_ROWS.filter((r) => passesExceptS(r, 'freq')).map((r) => r.freqKey));
    return FREQ_DEFS.filter((f) => keys.has(f.key));
  }, [ALL_ROWS, passesExceptS]);

  // Frequency chips are STATIC "quick view" summaries over the full dataset (buckets
  // always visible, full-dataset counts) — clicking one resets the other filters
  // (prototype parity). freqTotal = the "All schedules" count. (The Frequency COMBO,
  // freqOpts above, still cascades — that's the dropdown, a different control.)
  const freqChips = useMemo(() => FREQ_DEFS.filter((f) => ALL_ROWS.some((r) => r.freqKey === f.key)), [ALL_ROWS]);
  const freqCounts = useMemo(() => {
    const m = new Map<FreqKey, number>();
    for (const r of ALL_ROWS) m.set(r.freqKey, (m.get(r.freqKey) ?? 0) + 1);
    return m;
  }, [ALL_ROWS]);
  const freqTotal = ALL_ROWS.length;

  const anyFilter =
    clientSel.length || projectSel.length || siteSel.length ||
    freqSel.length || statusSel.length || search.trim().length > 0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ALL_ROWS.filter((r) => {
      if (clientSel.length && !clientSel.includes(r.client)) return false;
      if (projectSel.length && !projectSel.includes(r.projectId)) return false;
      if (siteSel.length && !siteSel.includes(r.name)) return false;
      if (freqSel.length && !freqSel.includes(r.freqKey)) return false;
      if (statusSel.length && !statusSel.includes(r.status)) return false;
      if (q) {
        const hay = `${r.cocId} ${r.name} ${r.client} ${r.projectId} ${r.recurrence}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [ALL_ROWS, clientSel, projectSel, siteSel, freqSel, statusSel, search]);

  const clearAll = () => {
    setClientSel([]); setProjectSel([]); setSiteSel([]);
    setFreqSel([]); setStatusSel([]); setSearch('');
  };

  // The frequency chips are quick "show only this frequency" views: clicking one
  // CLEARS the other filters and applies just that bucket (prototype parity);
  // "All schedules" / clicking the active chip clears the frequency filter.
  const toggleChip = (key: FreqKey | 'all') => {
    if (key === 'all') { clearAll(); return; }
    setClientSel([]); setProjectSel([]); setSiteSel([]); setStatusSel([]); setSearch('');
    setFreqSel((cur) => (cur.length === 1 && cur[0] === key ? [] : [key]));
  };
  const chipActive = (key: FreqKey | 'all') =>
    key === 'all' ? freqSel.length === 0 : freqSel.length === 1 && freqSel[0] === key;

  const refresh = () => {
    void load().then(() => { if (!error) notify.success('Refreshed.'); });
  };

  // Auto-sized columns (no forced widths) so the grid sizes to content and
  // horizontal-scrolls inside its own card, matching the prototype grid.
  // Cell styles mirror the prototype .grid: COC ID + Site/Name bold, secondary
  // text columns muted grey, Recurrence a monospace code chip, Frequency + Status
  // pills, numeric/date columns centered. `text` keeps filter/sort/export correct.
  const columns: AppColumn<SchedFreqRow>[] = useMemo(() => [
    { field: 'cocId', header: 'COC ID', render: (r) => <span className="ssf-cid">{r.cocId}</span>, text: (r) => r.cocId },
    {
      field: 'name', header: 'Site / Name',
      render: (r) => (
        <span className="ssf-nm">
          {r.name}
          {r.isLabel && (
            <span className="ssf-label-tag" title="No COC name in the source data — label built from client + recurrence">
              (label)
            </span>
          )}
        </span>
      ),
      text: (r) => r.name,
    },
    { field: 'client', header: 'Client', render: (r) => muted(r.client), text: (r) => r.client },
    { field: 'matrix', header: 'Matrix', render: (r) => muted(r.matrix), text: (r) => r.matrix },
    {
      field: 'recurrence', header: 'Recurrence',
      render: (r) => (r.recurrence
        ? <span className="ssf-rec">{r.recurrence}</span>
        : <span className="ssf-cell-muted">(no pattern)</span>),
      text: (r) => r.recurrence || '(no pattern)',
    },
    {
      field: 'freqKey', header: 'Frequency',
      render: (r) => {
        const label = r.frequency || FREQ_LABEL[r.freqKey] || '';
        return label ? <span className={`ssf-fp ${r.freqKey}`}>{label}</span> : null;
      },
      text: (r) => r.frequency || FREQ_LABEL[r.freqKey] || '',
    },
    { field: 'tat', header: 'TAT', align: 'center' },
    {
      // OG blanks a 0 "Days In Advance" (a Not-generating COC).
      field: 'daysAdv', header: '#Days Adv', align: 'center',
      render: (r) => (r.daysAdv && r.daysAdv !== '0' ? r.daysAdv : ''),
      text: (r) => (r.daysAdv && r.daysAdv !== '0' ? r.daysAdv : ''),
      compare: (a, b) => (Number(a.daysAdv) || 0) - (Number(b.daysAdv) || 0),
    },
    { field: 'priority', header: 'Priority' },
    {
      // Already formatted MM/DD/YYYY in mapRow; a "Not generating" COC has no
      // upcoming job, so show a muted dash. (No `type:'date'` — that would re-format.)
      field: 'nextUpcoming', header: 'Next Upcoming', align: 'center',
      render: (r) => (r.nextUpcoming ? <span>{r.nextUpcoming}</span> : muted('—')),
      text: (r) => r.nextUpcoming,
    },
    {
      // Auto-Generate Status: "Active" (green) when the COC pre-generates jobs
      // (DaysinAdvance > 0); "Not generating" is informational (neutral grey,
      // NOT the red "inactive" tone) — it just means no advance auto-generation.
      field: 'status', header: 'Status', align: 'center',
      render: (r) => (
        <span className={`ssf-st ${r.status.toLowerCase() === 'active' ? 'on' : 'muted'}`}
          title={r.status.toLowerCase() === 'active' ? undefined : 'No Days In Advance — jobs are not auto-generated ahead'}>
          {r.status}
        </span>
      ),
      text: (r) => r.status,
    },
    { field: 'projectId', header: 'ProjectID', render: (r) => muted(r.projectId || '—'), text: (r) => r.projectId },
    { field: 'projectName', header: 'ProjectName', render: (r) => muted(r.projectName || '—'), text: (r) => r.projectName },
    { field: 'projectCode', header: 'ProjectCode', render: (r) => muted(r.projectCode || '—'), text: (r) => r.projectCode },
  ], []);

  // Prototype "Export to CSV" — a single white ghost button (not the multi-format
  // "Export to ▾" dropdown), exporting exactly the visible/filtered rows via the
  // shared CSV exporter.
  const exportCsv = () => {
    const m = gridExportMatrix(visibleRows, columns);
    void runLoading(
      triggerGridExport('csv', 'Sample-Sites-Scheduling-Frequency', m.headers, m.rows)
        .then(() => notify.success('Exported to CSV successfully.'))
        .catch(() => notify.error('Export failed.')),
      { blocking: true, label: 'Exporting…' },
    );
  };

  return (
    <div className="bl-page ssf-scope">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Sample Scheduling</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Site Scheduling Frequency</span>
          </div>
          <div className="dp-title-row">
            {/* Read-only report — no mutations, so no audit log to surface (standing
                rule 11: audit shown only when audit-trail data exists). Matches the
                Sampling History twin. */}
            <FormTitle pageId="siteschedfreq" loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            Read-only · {loading ? '…' : ALL_ROWS.length} items
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        {/* Toolbar — search · Export to CSV sit directly NEXT TO Refresh on the left
            (user request 2026-09-19; the far-right spacer was dropped). The
            "Loaded in X" chip follows via the shared MetaChipsView, so the order is
            Refresh · Search · Export to CSV · Loaded. */}
        <AppToolbar
          onRefresh={refresh}
          refreshing={loading}
          right={
            <>
              <input
                className="ssf-search"
                type="text"
                placeholder="Search sites…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search sites"
              />
              <button className="ca-btn" onClick={exportCsv} title="Export to CSV">
                <span className="tb-i tb-i-teal">⬇</span> Export to CSV
              </button>
            </>
          }
        />

        {error && (
          <div className="bl-err">
            <i className="ti ti-alert-circle" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
            {error}
          </div>
        )}

        {/* ── Filter bar — bordered card of combos + Clear filters (prototype .filters) ── */}
        <div className="ssf-filters">
          <div className="ssf-fg">
            <label>Client</label>
            <CheckedCombo options={clientOpts} values={clientSel} onChange={setClientSel}
              placeholder="All client" unit="clients" ariaLabel="Filter by client" showCount />
          </div>
          <div className="ssf-fg">
            <label>Project ID</label>
            <CheckedCombo options={projectOpts} values={projectSel} onChange={setProjectSel}
              placeholder="All project id" unit="projects" ariaLabel="Filter by project id" showCount pageSize={12} />
          </div>
          <div className="ssf-fg">
            <label>Site</label>
            <CheckedCombo options={siteOpts} values={siteSel} onChange={setSiteSel}
              placeholder="All site" unit="sites" ariaLabel="Filter by site" showCount pageSize={12} />
          </div>
          <div className="ssf-fg">
            <label>Frequency</label>
            <CheckedCombo
              options={freqOpts.map((f) => f.label)}
              values={freqSel.map((k) => FREQ_LABEL[k])}
              onChange={(labels) => setFreqSel(
                FREQ_DEFS.filter((f) => labels.includes(f.label)).map((f) => f.key),
              )}
              placeholder="All frequency" unit="frequencies" ariaLabel="Filter by frequency" showCount />
          </div>
          <div className="ssf-fg">
            <label>Status</label>
            <CheckedCombo options={statusOpts} values={statusSel} onChange={setStatusSel}
              placeholder="All status" unit="statuses" ariaLabel="Filter by status" showCount />
          </div>
          <div className="ssf-fg ssf-actions">
            <label>&nbsp;</label>
            <button className="ca-btn" onClick={clearAll} disabled={!anyFilter} title="Clear filters"><i className="ti ti-filter-off" /> Clear filters</button>
          </div>
        </div>

        {/* ── Frequency summary chips ── */}
        <div className="ssf-chips">
          <button
            className={`ssf-chip${chipActive('all') ? ' active' : ''}`}
            onClick={() => toggleChip('all')}
          >
            ▦ All schedules <span className="ssf-chip-n">{freqTotal}</span>
          </button>
          {freqChips.map((f) => (
            <button
              key={f.key}
              className={`ssf-chip${chipActive(f.key) ? ' active' : ''}`}
              onClick={() => toggleChip(f.key)}
            >
              <span className={`ssf-fp ${f.variant}`}>{f.label}</span>
              <span className="ssf-chip-n">{freqCounts.get(f.key) ?? 0}</span>
            </button>
          ))}
        </div>

        {/* ── View-state line (prototype .viewstate) ── */}
        <p className="ssf-viewstate">
          {!anyFilter
            ? 'All schedules'
            : freqSel.length === 1 && !clientSel.length && !projectSel.length && !siteSel.length && !statusSel.length && !search.trim()
              ? FREQ_LABEL[freqSel[0]!]
              : 'Filtered'} · <b>{filtered.length}</b>
          {anyFilter && <span className="rst" onClick={clearAll}>↩ Clear filters</span>}
        </p>

        <div className="bl-card bl-card-flex">
          <AppDataGrid<SchedFreqRow>
            rows={filtered}
            columns={columns}
            getRowId={(r) => r.rk}
            loading={loading}
            initialSort={{ key: 'cocId', dir: 'asc' }}
            onVisibleRowsChange={setVisibleRows}
            emptyText="No schedules match the current filters"
          />
        </div>
      </div>
    </div>
  );
}
